import { useState, useEffect, useCallback } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { callDeleteUserAuthHttp, db } from "../config/firebase";

/**
 * Custom hook for user management operations
 */
export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to convert Firestore timestamps to readable dates
  const formatFirestoreDate = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";

    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
    }

    if (timestamp instanceof Date) {
      return timestamp.toLocaleString("he-IL");
    }

    try {
      return new Date(timestamp).toLocaleString("he-IL");
    } catch (error) {
      return "לא זמין";
    }
  }, []);

  // Real-time users listener
  useEffect(() => {
    const usersRef = collection(db, "users");
    const q = query(usersRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const usersData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            email: doc.id,
            ...data,
            createdAt: formatFirestoreDate(data.createdAt),
            updatedAt: formatFirestoreDate(data.updatedAt),
          };
        });

        setUsers(usersData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error("Error fetching users:", error);
        setError("שגיאה בטעינת המשתמשים");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [formatFirestoreDate]);

  // Add user
  const addUser = async (userData) => {
    try {
      const newUserData = {
        ...userData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const userRef = doc(db, "users", newUserData.email);
      await setDoc(userRef, newUserData);

      return { success: true };
    } catch (error) {
      console.error("Error adding user:", error);
      return { success: false, error: "שגיאה בהוספת משתמש" };
    }
  };

  // Update user
  const updateUser = async (userEmail, updateData) => {
    try {
      const userRef = doc(db, "users", userEmail);
      const finalUpdateData = {
        ...updateData,
        updatedAt: new Date(),
      };

      await updateDoc(userRef, finalUpdateData);

      return { success: true };
    } catch (error) {
      console.error("Error updating user:", error);
      return { success: false, error: "שגיאה בעדכון משתמש" };
    }
  };

  const deleteUser = async (userEmail) => {
    try {
      const targetUser = users.find((u) => u.email === userEmail);
      const uid = targetUser?.id !== userEmail ? targetUser?.id : null;

      await callDeleteUserAuthHttp({ uid, email: userEmail });

      await deleteDoc(doc(db, "users", userEmail));
      return { success: true };
    } catch (error) {
      console.error("Error deleting user:", error);
      return { success: false, error: "שגיאה במחיקת משתמש" };
    }
  };

  // Calculate statistics
  const statistics = {
    total: users.length,
    admin: users.filter((u) => u.permissions === "admin").length,
    parentsUser: users.filter((u) => u.permissions === "parentsUser").length,
    childUser: users.filter((u) => u.permissions === "childUser").length,
  };

  return {
    users,
    loading,
    error,
    statistics,
    addUser,
    updateUser,
    deleteUser,
  };
};
